<template>
  <div class="image-list-wrapper" ref="imageListWrapper" :style="{ '--column-width': columnWidth + 'px' }">
    <EmptyPage v-if="displayImageList.length === 0" :height="480" :loading="isLoading" />
    <div v-else>
      <div class="image-waterfall" :style="{ gap: columnGap + 'px' }">
        <div class="waterfall-column" v-for="(column, colIndex) in imageColumns" :key="'col-'+colIndex" :style="{ gap: columnGap + 'px' }">
          <div 
            class="image-list-item" 
            v-for="(item, index) in column" 
            :key="'img-'+colIndex+'-'+index" 
            @click="goDetail(item)"
          >
            <div 
              class="image-card"
              :class="{ 'square-image': item.square }"
            >
              <img 
                :src="`${item.logo}${item.logo.includes('?') ? '&' : '?'}x-oss-process=image/format,webp`" 
                alt="图片" 
                @load="setImage($event, item)"
                :class="{ 'wide-short-image': item.square }"
              />
              <div class="image-tag" v-if="item.aiGcLabel === 1">
                AIGC
              </div>
              <div class="image-name">{{ item.name }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="loading" v-show="isScrollLoading">
        <i class="el-icon-loading"></i>
      </div>
      <div v-if="noMoreData && useInternalLoading" class="no-more-data">没有更多数据了</div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getImageList, getCollectImageList, getOrderImageList } from "@/api/findImage/index";
import { openWindow } from "@/utils/utils";
import EmptyPage from "@/components/EmptyPage";

// 滚动防抖函数
function debounce(func, wait) {
  let timeout;
  return function() {
    const context = this;
    const args = arguments;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      func.apply(context, args);
    }, wait);
  };
}

export default {
  name: "ImageList",
  components: {
    EmptyPage,
  },
  props: {
    // 新增 props，允许外部传入列表数据
    externalImageList: {
      type: Array,
      default: () => []
    },
    externalLoading: {
      type: Boolean,
      default: false
    },
    externalScrollLoading: {
      type: Boolean,
      default: false
    },
    // 是否使用组件内部的数据加载逻辑
    useInternalLoading: {
      type: Boolean,
      default: true
    },
    // 搜索相关参数
    searchKeyword: {
      type: String,
      default: ''
    },
    // 筛选参数
    tab: {
      type: Object,
      default: () => ({
        categoryParentId: -1, // 一级分类ID
        categoryId: -1, // 二级分类ID
        formatId: -1, // 格式ID
        sort: '1', // 排序
        size: '', // 尺寸
        price: '', // 价格排序
        proportion: '0', // 比例(原scale参数)
        colorList: [] // 颜色列表
      }),
    },
    // 2: 我的收藏图片列表 1: 图片列表 3: 我的订单图片列表
    listType: {
      type: String,
      default: "1",
    },
    // 素材归属用户
    user: {
      type: Object,
      required: false,
    },
    // 是否是已收藏素材
    collected: {
      type: Boolean,
      required: false,
    },
    type: {
      type: [String, Number],
      required: false,
    },
  },
  inject: ["backTop"],
  data() {
    return {
      loading: false,
      scrollLoading: false,
      total: 0,
      pageSize: 80, // 每页加载数量
      pageNum: 1,
      pages: 1,
      // 图片列表
      imageList: [],
      // 需要显示详情的图片
      image: null,
      // 是否已经没有更多数据
      noMoreData: false,
      // 是否已经绑定了滚动事件
      scrollBound: false,
      // 列数
      columnCount: 4,
      // 记录上一次的滚动位置
      lastScrollTop: 0,
      // 防抖后的滚动处理函数
      debouncedScroll: null,
      // 测试计数器
      scrollCounter: 0,
      // 滚动容器
      scrollContainer: null,
      // 窗口宽度
      windowWidth: window.innerWidth,
      // 防抖处理的窗口大小变化函数
      debouncedResize: null,
      // 列宽
      columnWidth: 0,
      // 列间距
      columnGap: 20,
      // 上次更新布局的时间戳
      lastLayoutUpdateTime: 0,
      // 是否正在更新布局
      isUpdatingLayout: false,
      // 记录最短列的信息
      shortestColumnInfo: {
        index: 0,
        height: 0,
        element: null
      }
    };
  },
  computed: {
    ...mapState("app", ["scrollStatus"]),
    ...mapState("user", ["userInfo"]),
    // 根据 useInternalLoading 决定使用内部还是外部数据
    displayImageList() {
      return this.useInternalLoading ? this.imageList : this.externalImageList;
    },
    isLoading() {
      return this.useInternalLoading ? this.loading : this.externalLoading;
    },
    isScrollLoading() {
      return this.useInternalLoading ? this.scrollLoading : this.externalScrollLoading;
    },
    // 计算每列应该显示的图片 - 修改为优先填充最短列
    imageColumns() {
      const columns = Array.from({ length: this.columnCount }, () => []);
      const columnHeights = Array(this.columnCount).fill(0);
      
      // 将图片优先放入高度最小的列中
      this.displayImageList.forEach((image) => {
        // 找出当前高度最小的列
        const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
        // 将图片放入该列
        columns[minHeightIndex].push(image);
        // 更新该列的高度 (假设每张图片的高度与宽度成比例，可以用图片宽高比估算)
        // 如果图片没有高度信息，则假设其高度为列宽的0.8倍
        const estimatedHeight = image.height && image.width 
          ? (image.height / image.width) * this.columnWidth 
          : this.columnWidth * 0.8;
        
        columnHeights[minHeightIndex] += estimatedHeight + this.columnGap;
      });
      
      return columns;
    }
  },
  watch: {
    // 添加对 externalImageList 的监听
    externalImageList: {
      handler(newVal) {
        if (!this.useInternalLoading && newVal.length > 0) {
          // 外部数据更新时，可能需要执行一些操作
          this.$nextTick(() => {
            this.updateColumnCount();
          });
        }
      }
    },
    // 添加对 searchKeyword 的监听
    searchKeyword: {
      handler(newVal) {
        if (this.useInternalLoading && newVal) {
          // 搜索关键词变化时重新加载
          this.resetPagination();
          this.getImageList();
        }
      }
    },
    tab: {
      deep: true,
      immediate: true,
      handler(newVal, oldVal) {
        if (!this.useInternalLoading) return; // 如果不使用内部加载逻辑，则忽略
        
        // 重置分页数据
        this.resetPagination();

        // 检查是否有有效的筛选条件
        if (this.hasValidFilter(newVal)) {
          this.getImageList();
        }
      },
    },
    type: {
      immediate: true,
      handler(newVal) {
        if (!this.useInternalLoading) return; // 如果不使用内部加载逻辑，则忽略
        
        if (newVal) {
          this.resetPagination();
          this.getImageList();
        }
      },
    },
    // 监听窗口宽度变化，更新列数和列宽
    windowWidth: {
      handler(newWidth) {
        // 当窗口宽度变化时，更新列数和列间距
        const oldColumnCount = this.columnCount;
        this.columnCount = newWidth >= 1920 ? 5 : 4;
        this.columnGap = newWidth >= 1920 ? 16 : 20;
        
        // 只有列数变化时才全面更新布局
        if (oldColumnCount !== this.columnCount) {
          this.updateColumnWidth();
        }
      }
    }
  },
  created() {
    // 创建防抖处理的滚动函数
    this.debouncedScroll = debounce(this.handleScroll, 100);
    // 创建防抖处理的窗口大小变化函数 - 使用更长的延迟以减少频繁更新
    this.debouncedResize = debounce(this.handleResize, 200);
  },
  mounted() {
    // 初始化布局参数
    this.initializeLayoutParameters();
    
    // 如果使用内部加载逻辑，则绑定滚动事件和初始化列数
    if (this.useInternalLoading) {
      // 绑定滚动事件
      this.bindScrollEvent();
      
      // 添加测试代码，每秒检查一次滚动事件是否正常工作
      this.scrollTestInterval = setInterval(() => {
        // 计数器自增即可，无需输出
        this.scrollCounter++;
      }, 5000);
    }
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.onWindowResize, { passive: true });
  },
  beforeDestroy() {
    // 组件销毁前移除滚动事件监听
    this.removeScrollEvent();
    if (this.scrollTestInterval) {
      clearInterval(this.scrollTestInterval);
    }
    
    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    // 初始化布局参数
    initializeLayoutParameters() {
      // 初始化列数和列间距
      this.columnCount = this.windowWidth >= 1920 ? 5 : 4;
      this.columnGap = this.windowWidth >= 1920 ? 16 : 20;
      
      // 计算列宽
      this.$nextTick(() => {
        this.updateColumnWidth();
      });
    },
    
    // 更新列宽
    updateColumnWidth() {
      if (!this.$refs.imageListWrapper) return;
      
      const now = Date.now();
      // 如果两次更新的时间间隔过短，或者正在更新，则跳过
      if (now - this.lastLayoutUpdateTime < 100 || this.isUpdatingLayout) return;
      
      this.isUpdatingLayout = true;
      this.lastLayoutUpdateTime = now;
      
      // 使用 requestAnimationFrame 确保在浏览器重绘前更新
      requestAnimationFrame(() => {
        const wrapperWidth = this.$refs.imageListWrapper.clientWidth;
        const totalGapWidth = this.columnGap * (this.columnCount - 1);
        this.columnWidth = Math.floor((wrapperWidth - totalGapWidth) / this.columnCount);
        this.isUpdatingLayout = false;
      });
    },
    
    // 窗口大小变化事件 - 仅做窗口宽度的获取，具体处理通过 watch 和防抖函数处理
    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.debouncedResize();
    },

    // 处理窗口大小变化 - 防抖后执行
    handleResize() {
      this.updateColumnWidth();
    },

    // 获取滚动容器
    getScrollContainer() {
      this.scrollContainer = document.getElementsByClassName('app-layout')[0];
      if (!this.scrollContainer) {
        // 仅保留错误日志
        console.error("[ImageList] 无法找到滚动容器 app-layout，将使用 document 作为滚动容器");
        this.scrollContainer = document.documentElement;
      }
      return this.scrollContainer;
    },

    // 绑定滚动事件
    bindScrollEvent() {
      if (!this.scrollBound) {
        const container = this.getScrollContainer();
        // 监听滚动容器的滚动事件
        container.addEventListener("scroll", this.onWindowScroll, { passive: true });
        
        // 监听鼠标滚轮事件
        container.addEventListener("wheel", this.onWindowScroll, { passive: true });
        
        // 同时也监听触摸事件（移动设备）
        container.addEventListener("touchmove", this.onWindowScroll, { passive: true });
        
        this.scrollBound = true;
        
        // 立即检查初始滚动位置
        setTimeout(() => {
          this.handleScroll({ type: 'init' });
        }, 500);
      }
    },

    // 移除滚动事件
    removeScrollEvent() {
      if (this.scrollBound && this.scrollContainer) {
        this.scrollContainer.removeEventListener("scroll", this.onWindowScroll);
        this.scrollContainer.removeEventListener("wheel", this.onWindowScroll);
        this.scrollContainer.removeEventListener("touchmove", this.onWindowScroll);
        this.scrollBound = false;
      }
    },
    
    // 滚动事件初始处理
    onWindowScroll(event) {
      // 增加计数器
      this.scrollCounter++;
      
      // 使用防抖函数处理滚动
      this.debouncedScroll(event);
    },

    // 处理滚动事件
    handleScroll(event) {
      // 如果正在加载或者已经没有更多数据，则不处理
      if (this.loading || this.scrollLoading || this.noMoreData) {
        return;
      }

      // 获取滚动信息
      const container = this.scrollContainer || document.documentElement;
      const scrollTop = container.scrollTop;
      const windowHeight = container.clientHeight;
      
      // 获取最短列信息
      const shortestColumnInfo = this.getShortestColumnInfo();
      
      if (shortestColumnInfo && shortestColumnInfo.element) {
        // 获取最短列的最后一张图片位置
        const rect = shortestColumnInfo.element.getBoundingClientRect();
        const bottomPosition = rect.top + rect.height;
        
        // 当滚动位置距离最短列最后一张图片顶部500px以内时加载下一页
        if (bottomPosition - windowHeight < 800) {
          this.loadMore();
        }
      } else {
        // 如果无法获取最短列信息，退回到距离底部的判断
        const documentHeight = container.scrollHeight;
        const distanceToBottom = documentHeight - (scrollTop + windowHeight);
        if (distanceToBottom < 600) {
          this.loadMore();
        }
      }
    },

    // 获取最短列及其最后一张图片的信息
    getShortestColumnInfo() {
      try {
        // 获取所有列元素
        const columnElements = this.$el.querySelectorAll('.waterfall-column');
        if (!columnElements || columnElements.length === 0) return null;
        
        let shortestIndex = 0;
        let shortestHeight = Infinity;
        
        // 找出高度最小的列
        Array.from(columnElements).forEach((column, index) => {
          if (column.offsetHeight < shortestHeight) {
            shortestHeight = column.offsetHeight;
            shortestIndex = index;
          }
        });
        
        // 获取最短列的最后一个图片元素
        const shortestColumn = columnElements[shortestIndex];
        const imageItems = shortestColumn.querySelectorAll('.image-list-item');
        const lastImageItem = imageItems[imageItems.length - 1];
        
        return {
          index: shortestIndex,
          height: shortestHeight,
          element: lastImageItem
        };
      } catch (error) {
        console.error('获取最短列信息失败:', error);
        return null;
      }
    },

    // 加载更多数据
    loadMore() {
      // 如果已经到最后一页，则不再加载
      if (this.pageNum >= this.pages) {
        this.noMoreData = true;
        return;
      }

      // 防止重复加载
      if (this.scrollLoading) {
        return;
      }

      this.pageNum++;
      this.scrollLoading = true;
      this.getImageList(true);
    },

    // 重置分页数据
    resetPagination() {
      this.imageList = [];
      this.total = 0;
      this.pageNum = 1;
      this.pages = 1;
      this.noMoreData = false;
    },

    // 检查是否有有效的筛选条件
    hasValidFilter(tab) {
      // 允许任何有效的筛选条件加载数据
      if (tab.categoryParentId !== -1 || tab.categoryId !== -1 || tab.formatId !== -1) {
        return true;
      }
      
      // 检查其他筛选条件
      if (tab.sort && tab.sort !== '1') {
        return true;
      }
      
      if (tab.size) {
        return true;
      }
      
      if (tab.price) {
        return true;
      }
      
      if (tab.proportion && tab.proportion !== '0') {
        return true; 
      }
      
      if (tab.colorList && tab.colorList.length > 0) {
        return true;
      }
      
      // 如果没有筛选条件，仍然允许加载（显示全部）
      return true;
    },

    /**
     * 回填图片列表（用户可能在详情页收藏或下载，列表需同步更新）
     */
    updateImageList(newImageInfo) {
      // 有id时更新指定图片信息
      if (newImageInfo.id) {
        const image = this.imageList.find((image) => image.id === newImageInfo.id);
        if (image) {
          Object.assign(image, newImageInfo);
        }
      } else {
        // 否则批量更新
        this.imageList.forEach((image) => Object.assign(image, newImageInfo));
      }
    },

    // 获取图片列表
    getImageList(isLoadMore = false) {
      if (!this.useInternalLoading) return; // 如果不使用内部加载逻辑，则直接返回
      
      if (this.loading && !isLoadMore) return;
      
      if (!isLoadMore) {
        this.loading = true;
      }

      // 构建查询参数
      const params = {
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        userId: (this.userInfo||{}).id || '',
      };

      // 添加搜索关键词
      if (this.searchKeyword) {
        params.title = this.searchKeyword;
      }

      // 只添加有效的筛选参数（不为-1的参数）
      if (this.tab.categoryParentId !== undefined && this.tab.categoryParentId !== -1) {
        params.categoryParentId = this.tab.categoryParentId || '';
      }

      if (this.tab.categoryId !== undefined && this.tab.categoryId !== -1) {
        params.categoryId = this.tab.categoryId || '';
      }

      if (this.tab.formatId !== undefined && this.tab.formatId !== -1) {
        params.formatId = this.tab.formatId || '';
      }

      // 添加排序参数
      if (this.tab.sort && this.tab.sort !== '1') {
        params.sort = this.tab.sort || '';
      }

      // 添加尺寸参数
      if (this.tab.size) {
        params.size = this.tab.size || '';
      }

      // 添加价格排序参数
      if (this.tab.price) {
        params.price = this.tab.price || '';
      }

      // 添加比例参数
      if (this.tab.proportion) {
        params.proportion = this.tab.proportion || '';
      }

      // 添加颜色参数
      if (this.tab.colorList && this.tab.colorList.length > 0) {
        params.colorList = this.tab.colorList.join(',');
      }

      // 添加其他可能的参数
      if (this.type) {
        params.type = this.type || '';
      }

      if (this.collected) {
        params.collected = this.collected || '';
      }

      let api = getImageList;
      if (this.listType === "2") {
        api = getCollectImageList;
      } else if (this.listType === "3") {
        api = getOrderImageList;
      }
      api(params)
        .then(({ data }) => {
          const result = data.result || data;

          if (isLoadMore) {
            // 加载更多时，追加数据
            this.imageList = [...this.imageList, ...result];
          } else {
            // 首次加载时，替换数据
            this.imageList = result;
          }

          this.total = data.total || data.length;
          this.pages = data.pages || 1;

          // 判断是否还有更多数据
          if (this.pageNum >= this.pages) {
            this.noMoreData = true;
          }
        })
        .catch(err => {
          console.error("[ImageList] 请求失败:", err);
        })
        .finally(() => {
          this.loading = false;
          this.scrollLoading = false;
        });
    },

    goDetail(image) {
      if (!image.id) {
        return;
      }
      openWindow(`/#/findImage/detail/${image.id}`);
    },
    
    // 处理图片加载完成，检测宽高比并设置对应的类
    setImage(event, item) {
      // 使用 Vue 的响应式特性代替直接操作 DOM
      const imgElement = event.target;
      
      // 获取图片实际宽高，并保存到图片对象中以便计算列高度
      const imageList = this.useInternalLoading ? this.imageList : this.externalImageList;
      const index = imageList.findIndex(image => image.id === item.id);
      
      if (index !== -1) {
        // 保存图片的自然宽高用于计算
        this.$set(imageList[index], 'width', imgElement.naturalWidth);
        this.$set(imageList[index], 'height', imgElement.naturalHeight);
        
        // 检测图片宽高比例，设置正方形标记属性
        if (imgElement.naturalHeight < 200 && imgElement.naturalWidth > imgElement.naturalHeight) {
          this.$set(imageList[index], 'square', true);
        }
        
        // 如果是新加载的图片，并且接近视口底部，检查是否需要加载更多
        this.$nextTick(() => {
          if (index >= this.displayImageList.length - this.pageSize) {
            this.handleScroll({ type: 'imageLoaded' });
          }
        });
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.image-list-wrapper {
  margin-top: 20px;
  
  .image-waterfall {
    display: flex;
    width: 100%;
  }

  .waterfall-column {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .image-list-item {
    width: 100%;
  }

  .image-card {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    transition: all 0.3s;
    cursor: pointer;
    position: relative;
    background-image: linear-gradient(to top right, #d4d4d4 25%, transparent 0, transparent 75%, #d4d4d4 0, #d4d4d4), linear-gradient(to top right, #d4d4d4 25%, transparent 0, transparent 75%, #d4d4d4 0, #d4d4d4);
    background-color: #dfdfdf;
    background-size: 16px 16px;
    background-position: 0 0, 8px 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    
    &.square-image {
      height: var(--column-width);
    }
    
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
      .image-name {
        display: block;
      }
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 8px;
      }
    }

    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
      border-radius: 8px;
      
      &.wide-short-image {
        height: 100%;
        object-fit: contain;
      }
    }
    .image-tag {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 40px;
      height: 20px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 6px;
      color: #fff;
      font-size: 12px;
      font-weight: bold;
      text-align: center;
      line-height: 20px;
    }
    .image-name {
      display: none;
      position: absolute;
      bottom: 4px;
      left: 0px;
      padding:0 30px 4px 12px;
      color: #fff;
      font-size: 14px;
      width: 100%;
      line-height: 20px;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .loading {
    text-align: center;
    margin: 20px 0;
    font-size: 24px;
    color: #2f54eb;
  }

  .no-more-data {
    text-align: center;
    margin: 20px 0;
    color: #999;
    font-size: 14px;
  }
}

/* 响应式布局 */
@media screen and (min-width: 1920px) {
  .image-list-wrapper {
    .image-waterfall {
      gap: 16px;
    }
    .waterfall-column {
      gap: 16px;
    }
  }
}

@include mq($smaller)  {
  .image-list-wrapper {
    .image-waterfall {
      gap: 15px;
    }
    .waterfall-column {
      gap: 15px;
    }
  }
}

@media screen and (max-width: 992px) {
  .image-list-wrapper {
    .image-waterfall {
      gap: 15px;
    }
  }
}

@media screen and (max-width: 768px) {
  .image-list-wrapper {
    .image-waterfall {
      gap: 10px;
    }
    .waterfall-column {
      gap: 10px;
    }
  }
}
</style>
